import { AbsgArrayHelper, CacheService, EnvironmentConfiguration, HttpExtra, LogService } from '@absg/angular-common';
import { MSODataQuery, ODataResult, ODataSearchList } from '@absg/angular-components';
import { CreateLookupCode, LookupDetail, LookupsApi, LookupsODataApi } from '@absg/api-lookup';
import { Allergy, AllergyLookupService, BaseService, LookupTypes, PickListItem } from '@absg/nim-client-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DisplayAllergy } from 'shared.root/models/display-allergy.model';

@Injectable({ providedIn: 'root' })
export class LookupService extends BaseService {
  endpointUrl: string;
  key = 'lookup-reasons';
  reportFilterList$ = new BehaviorSubject<LookupDetail[]>([]);
  constructor(
    private lookupsApi: LookupsApi,
    private lookupsODataApi: LookupsODataApi,
    private allergyLookupService: AllergyLookupService,
    private environment: EnvironmentConfiguration,
    loggingService: LogService,
    private cacheService: CacheService,
  ) {
    super(loggingService);
    this.endpointUrl = this.environment.apis.lookup.endpointUrl;
  }
  getLookupsFromCache(): Observable<LookupDetail[]> {
    // if in cache then return
    const result = this.cacheService.get(this.key);
    if (result.value !== null) {
      return of(result.value as LookupDetail[]);
    }
    // cache miss. call api and then cache it
    const lookupTypes = [
      LookupTypes.ReturnReason, LookupTypes.WasteReason,
      LookupTypes.ClinicChargeReason, LookupTypes.DiscrepancyReason,
      LookupTypes.PatientOwnedItemType, LookupTypes.JcodeExclusion,
      LookupTypes.TransferReason, LookupTypes.TransferOutReason,
      LookupTypes.ClinicChargeReturnReasons, LookupTypes.RestockReason,
      LookupTypes.BarcodeOverrideReason,
    ];

    const query = new MSODataQuery({
      searchFields: [],
      pageNumber: 1,
      itemsPerPage: 200,
      excludeCount: true,
      inSearch: [new ODataSearchList('listTypeId', lookupTypes)],
    });

    return this.lookupsODataApi.getLookupsOData({ search: query.toString() })
      .pipe(
        map((response) => {
          this.cacheService.set(this.key, response.value);
          return response.value;
        }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }
  getReturnReasons(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map((response) => response as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.ReturnReason))),
        map((items: PickListItem[]) => this.sortList(items)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getWasteReasons(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map((response) => response as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.WasteReason))),
        map((items: PickListItem[]) => this.sortList(items)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getClinicChargeReasons(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map((response) => response as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.ClinicChargeReason))),
        map((items: PickListItem[]) => this.sortList(items)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getClinicChargeReturnReasons(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map((response) => response as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.ClinicChargeReturnReasons))),
        map((items: PickListItem[]) => this.sortList(items)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getBarcodeOverrideReasons(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map((response) => response as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.BarcodeOverrideReason))),
        map((items: PickListItem[]) => this.sortList(items)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getDiscrepancyReasons(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map((response) => response as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.DiscrepancyReason))),
        map((items: PickListItem[]) => this.sortList(items)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getTransferDiscrepancyReasons(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map(response => AbsgArrayHelper.orderBy(response, (elem) => [elem.name?.toLowerCase()]) as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.TransferReason))),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getTransferOutReasons(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map(response => AbsgArrayHelper.orderBy(response, (elem) => [elem.name?.toLowerCase()]) as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.TransferOutReason))),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getPatientOwnedItemTypeDetailList(): Observable<LookupDetail[]> {
    return this.getLookupsFromCache()
      .pipe(
        map((response) => response as LookupDetail[]),
        map((lookups: LookupDetail[]) => lookups.filter(p => p.listTypeId === LookupTypes.PatientOwnedItemType)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getPatientOwnedItemType(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map((response) => response as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.PatientOwnedItemType))),
        map((items: PickListItem[]) => this.sortList(items)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getJcodeExclusionList(): Observable<PickListItem[]> {
    return this.getLookupsFromCache()
      .pipe(
        map((response) => response as LookupDetail[]),
        map((lookups: LookupDetail[]) => this.convertToPickListItems(lookups.filter(p => p.listTypeId === LookupTypes.JcodeExclusion))),
        map((items: PickListItem[]) => this.sortList(items)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  searchAllergies(httpParams: HttpExtra = { search: '' }): Observable<ODataResult<DisplayAllergy>> {
    return this.lookupsODataApi.getLookupsOData(httpParams)
      .pipe(
        map((response) => this.mapResponse(ODataResult.convertFromMSODataResult(response as ODataResult<DisplayAllergy>))),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  createAllergy(allergyName: string): Observable<DisplayAllergy> {
    const lookup: CreateLookupCode = {
      listTypeId: LookupTypes.Allergy,
      name: allergyName,
      shortName: allergyName,
    };

    return this.lookupsApi.addLookupCode(lookup)
      .pipe(
        map(response => this.mapNewAllergyResponse(response)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      ) as Observable<DisplayAllergy>;
  }

  getAllergiesByInclusionIds(inclusionIds: number[]): Observable<Allergy[]> {
    return this.allergyLookupService.getAllergiesByInclusionIds(inclusionIds)
      .pipe(
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  private mapNewAllergyResponse(newAllergy: LookupDetail): DisplayAllergy {
    const mappedNewAllergy =
      {
        listInclusionId: newAllergy.listInclusionId,
        listTypeId: newAllergy.listTypeId,
        lookupCodeId: newAllergy.lookupCodeId,
        name: newAllergy.name,
        shortName: newAllergy.shortName,
        selected: false,
        disabled: false,
      } as DisplayAllergy;
    return mappedNewAllergy;
  }

  private mapResponse(response: ODataResult<LookupDetail>): ODataResult<DisplayAllergy> {
    const result = new ODataResult<DisplayAllergy>();
    result.value = [];
    result['odata.count'] = response['odata.count'];
    result.value = AbsgArrayHelper.map(AbsgArrayHelper.sortBy(response.value, x => [x.name.toLowerCase()]), x => {
      const allergy =
        {
          listInclusionId: x.listInclusionId,
          listTypeId: x.listTypeId,
          lookupCodeId: x.lookupCodeId,
          name: x.name,
          shortName: x.shortName,
          selected: false,
          disabled: false,
        } as DisplayAllergy;
      return allergy;
    });

    return result;

  }

  private sortList(items: PickListItem[]): PickListItem[] {
    return items.sort((item1, item2) => item1.text < item2.text ? -1 : 1);
  }

  private convertToPickListItems(lookups: LookupDetail[]): PickListItem[] {
    const listItems: PickListItem[] = [];

    lookups.forEach(lookup => listItems.push(new PickListItem(lookup.listInclusionId, lookup.name)));

    return listItems;
  }
}
